<template>
  <div
    id="game-server-index-view"
  >
    <Jumbotron
      height="200"
      :src="`${config.IMAGE_SERVICE_URL}/games/${item.data.identifier}/background.jpg`"
    >
      <v-container
        class="mx-auto standard-page-width"
        fill-height
        fluid
      >
        <v-row
          align="center"
          justify="center"
        >
          <v-col>
            <v-avatar
              class="elevation-3"
              size="64"
            >
              <img
                :src="`${config.IMAGE_SERVICE_URL}/games/${item.data.identifier}/icon.png`"
              >
            </v-avatar>
            <span
              class="ml-2 white--text text-h6"
              :style="{ textShadow: '1px 1px #000' }"
            >
              {{ item.data.name }} {{ $t('servers') }}
            </span>
          </v-col>
        </v-row>
      </v-container>
    </Jumbotron>
    <v-container
      class="standard-page-width"
      fluid
    >
      <v-card
        v-if="!hasLoaded"
        elevation="1"
      >
        <v-card-text>
          <v-progress-linear
            class="my-3"
            color="secondary"
            indeterminate
          />
        </v-card-text>
      </v-card>
      <v-row
        v-else
        :class="{ 'flex-row-reverse': $vuetify.breakpoint.smAndDown }"
      >
        <v-col
          :cols="$vuetify.breakpoint.mdAndUp ? 8 : 12"
        >
          <v-card
            v-if="isLoading"
            elevation="1"
          >
            <v-card-text>
              <v-progress-linear
                class="my-3"
                color="secondary"
                indeterminate
              />
            </v-card-text>
          </v-card>
          <template
            v-else
          >
            <v-card
              class="mb-4"
              elevation="1"
            >
              <v-card-text>
                {{ $tc('servers_found', totalItems) }}
              </v-card-text>
            </v-card>
            <v-card
              v-if="totalItems < 1"
              class="mt-4"
              elevation="1"
            >
              <v-card-text>
                {{ $t('no_results_found') }}
              </v-card-text>
            </v-card>
            <template
              v-else
            >
              <v-card
                v-for="(server, index) in items"
                :key="server.data.id"
                :class="index !== 0 ? 'mt-4' : ''"
                elevation="1"
                :to="{ name: 'server.show', params: { serverId: server.data.id } }"
              >
                <v-row
                  :class="{ 'flex-nowrap': $vuetify.breakpoint.mdAndUp }"
                >
                  <v-col
                    class="py-0 overflow-hidden"
                    :cols="$vuetify.breakpoint.mdAndUp ? false : 12"
                  >
                    <ServerPreviewCard
                      color="transparent"
                      flat
                      :game="server.data.game"
                      :item="server"
                    />
                  </v-col>
                  <v-col
                    class="flex-grow-0 py-0"
                    :cols="$vuetify.breakpoint.mdAndUp ? false : 12"
                  >
                    <v-card-text
                      :class="{ 'pt-0': $vuetify.breakpoint.smAndDown }"
                    >
                      <ServerLikeButton
                        :server="server"
                        :vertical="$vuetify.breakpoint.smAndDown"
                      />
                    </v-card-text>
                  </v-col>
                </v-row>
                <v-divider />
              </v-card>
            </template>
            <v-card
              class="mt-4 text-center"
              elevation="1"
            >
              <v-card-text>
                <PaginationControl
                  :page="queryParams.page"
                  :per-page="queryParams.per_page"
                  :total="totalItems"
                  @select="handleFilterInput('page', $event)"
                />
              </v-card-text>
            </v-card>
          </template>
        </v-col>
        <v-col
          :cols="$vuetify.breakpoint.mdAndUp ? 4 : 12"
        >
          <v-card
            elevation="1"
          >
            <v-card-title>
              {{ $t('filters') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-text-field
                clearable
                hide-details
                :label="$t('name')"
                outlined
                :value="$route.query.name"
                @input="handleFilterInput('name', $event)"
              />
              <v-text-field
                class="mt-3"
                clearable
                hide-details
                :label="`${$t('player_count')} (${$t('min')})`"
                outlined
                type="number"
                :value="$route.query.min_player_count"
                @input="handleFilterInput('min_player_count', $event)"
              />
              <v-text-field
                class="mt-3"
                clearable
                hide-details
                :label="`${$t('player_count')} (${$t('max')})`"
                outlined
                type="number"
                :value="$route.query.max_player_count"
                @input="handleFilterInput('max_player_count', $event)"
              />
              <template
                v-if="displayMoreOptions"
              >
                <v-divider
                  class="my-4"
                />
                <v-text-field
                  clearable
                  hide-details
                  :label="$t('host')"
                  outlined
                  :value="$route.query.host"
                  @input="handleFilterInput('host', $event)"
                />
                <v-text-field
                  class="mt-3"
                  clearable
                  hide-details
                  :label="$t('port')"
                  outlined
                  type="number"
                  :value="$route.query.port"
                  @input="handleFilterInput('port', $event)"
                />
              </template>
            </v-card-text>
            <v-card-actions
              class="text-center"
            >
              <v-btn
                color="secondary"
                text
                @click="displayMoreOptions = !displayMoreOptions"
              >
                {{ displayMoreOptions ? $t('show_less') : $t('show_more') }}
              </v-btn>
            </v-card-actions>
          </v-card>
          <v-card
            class="mt-4"
            elevation="1"
          >
            <v-card-title>
              {{ $t('sort') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
              <v-select
                hide-details
                :items="[{ text: $t('player_count'), value: 'player_count' }, { text: 'Likes', value: 'likes_count' }]"
                :label="$t('by')"
                outlined
                :value="queryParams.sort_by"
                @input="handleFilterInput('sort_by', $event)"
              />
              <v-select
                class="mt-3"
                hide-details
                :items="[{ text: $t('ascending'), value: 'asc' }, { text: $t('descending'), value: 'desc' }]"
                :label="$t('order')"
                outlined
                :value="queryParams.sort_order"
                @input="handleFilterInput('sort_order', $event)"
              />
            </v-card-text>
          </v-card>
          <v-card
            class="mt-4"
            elevation="1"
          >
            <v-card-text>
              <v-btn
                block
                color="secondary"
                :to="{ name: 'server.create' }"
              >
                {{ $t('submit_a_server') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { Game, Server } from '@serversgg/sdk-js'

import Jumbotron from '@/components/Jumbotron'
import PaginationControl from '@/components/PaginationControl'
import ServerLikeButton from '@/components/ServerLikeButton'
import ServerPreviewCard from '@/components/ServerPreviewCard'

export default {
  name: 'GameServerIndexView',

  components: {
    Jumbotron,
    PaginationControl,
    ServerLikeButton,
    ServerPreviewCard,
  },

  data () {
    return {
      baseParams: {
        include: [
          'game',
        ],
        is_online: 1,
        page: 1,
        per_page: 25,
        sort_by: 'likes_count',
        sort_order: 'desc',
      },
      config: {
        IMAGE_SERVICE_URL: process.env.IMAGE_SERVICE_URL || 'https://img.servers.gg',
      },
      displayMoreOptions: false,
      hasLoaded: false,
      isLoading: false,
      item: new Game(),
      items: null,
      models: {
        Game,
      },
    }
  },

  computed: {
    queryParams () {
      return {
        ...this.baseParams,
        language: this.$i18n.locale,
        ...this.$route.query,
        game_id: this.item.data.id,
      }
    },

    totalItems () {
      return this.items
        ? this.items.getTotal()
        : 0
    },
  },

  watch: {
    async queryParams () {
      await this.loadItems()
    },
  },

  metaInfo () {
    return {
      title: this.item.exists()
        ? `${this.item.data.name} ${this.$t('servers')}`
        : `${this.$t('loading')}..`,
    }
  },

  async beforeRouteEnter (to, from, next) {
    const items = await Game
      .request()
      .setInclude('game')
      .fetch({
        identifier: to.params.gameIdentifier,
      })

    // eslint-disable-next-line no-return-assign
    next((vm) => vm.item = items.first())
  },

  async beforeRouteUpdate (to, from, next) {
    if (to.params.gameIdentifier === from.params.gameIdentifier) {
      await this.loadItems()
      next()
      return
    }

    Object.assign(this.$data, this.$options.data.call(this))

    const items = await Game
      .request()
      .setInclude('game')
      .fetch({
        identifier: to.params.gameIdentifier,
      })

    this.item = items.first()
    next()
  },

  methods: {
    async handleFilterInput (key, value) {
      const query = {
        ...this.$route.query,
        [key]: value,
      }

      if (value === '') {
        delete query[key]
      }

      if (
        key !== 'page' &&
        typeof query.page !== 'undefined'
      ) {
        delete query.page
      }

      if (
        key === 'sort_by' &&
        value === this.baseParams.sort_by &&
        typeof query.sort_by !== 'undefined'
      ) {
        delete query.sort_by
      }

      if (
        key === 'sort_order' &&
        value === this.baseParams.sort_order &&
        typeof query.sort_order !== 'undefined'
      ) {
        delete query.sort_order
      }

      try {
        await this.$router.push({
          name: this.$route.name,
          query,
        })
      } catch (e) {
        // Do nothing.
      }
    },

    async loadItems () {
      this.isLoading = true

      try {
        this.items = await Server
          .request()
          .fetch(this.queryParams)
      } catch (e) {
        // Oh noes.
      } finally {
        this.hasLoaded = true
        this.isLoading = false
      }
    },
  },
}
</script>
